(() => {
  if (customElements.get('tb-audio-preview')) return;
  const previews = new Set();
  const durations = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 749px)');
  const storageKey = 'timpani-preview-volume';
  const playbackKey = 'timpani-preview-session-v1';
  const playbackMaxAge = 2 * 60 * 60 * 1000;
  const playbackSaveInterval = 5000;
  const labelKeys = ['playerLabel', 'seekLabel', 'closeLabel', 'volumeLabel', 'muteLabel', 'unmuteLabel', 'playLabel', 'pauseLabel', 'errorLabel'];
  const cleanText = (value, limit = 240) => typeof value === 'string'
    ? value.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, limit) : '';
  const audioURL = value => {
    if (typeof value !== 'string' || value.length > 2048) return '';
    try {
      const url = new URL(value, location.origin);
      if (url.protocol !== 'https:' || url.username || url.password || url.hash || url.port) return '';
      const storeFile = url.origin === location.origin && url.pathname.startsWith('/cdn/shop/files/');
      const shopifyFile = url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/s/files/');
      return (storeFile || shopifyFile) && /\.(mp3|wav|m4a|aac|ogg|oga|flac)$/i.test(url.pathname) ? url.href : '';
    } catch { return ''; }
  };
  const productURL = value => {
    if (typeof value !== 'string' || !value || value.length > 2048) return '';
    try {
      const url = new URL(value, location.origin);
      return url.origin === location.origin && !url.username && !url.password
        && /^\/products\/[^/]+\/?$/.test(url.pathname) ? url.origin + url.pathname : '';
    } catch { return ''; }
  };
  // The selected track is a plain descriptor; its controls never depend on a card surviving.
  const trackData = data => {
    const title = cleanText(data.title, 200);
    const defaults = {
      playerLabel: 'Audio preview player', seekLabel: 'Seek preview: ' + title,
      closeLabel: 'Close player', volumeLabel: 'Preview volume', muteLabel: 'Mute preview',
      unmuteLabel: 'Unmute preview', playLabel: 'Play preview: ' + title,
      pauseLabel: 'Pause preview: ' + title, errorLabel: 'Preview unavailable. Please try again.'
    };
    const dataset = { audioSrc: data.audioSrc, title, productUrl: productURL(data.productUrl) };
    for (const key of labelKeys) dataset[key] = cleanText(data[key]) || defaults[key];
    return { dataset };
  };
  const format = value => Number.isFinite(value) && value >= 0
    ? Math.floor(value / 60) + ':' + String(Math.floor(value % 60)).padStart(2, '0') : '—:—';
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const icons = {
    play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5l11 7-11 7Z" fill="currentColor"/></svg>',
    pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7Zm6 0h4v14h-4Z" fill="currentColor"/></svg>',
    speaker: '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H3v6h3l5 4Z"/><path class="tb44-speaker-waves" d="M15 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14"/><path class="tb44-speaker-cross" d="m16 9 6 6m0-6-6 6"/></svg>'
  };
  const attr = (el, key, value) => {
    if (el && el.getAttribute(key) !== String(value)) el.setAttribute(key, String(value));
  };
  const text = (el, value) => { if (el && el.textContent !== value) el.textContent = value; };
  const property = (el, key, value) => {
    if (el && el.style.getPropertyValue(key) !== value) el.style.setProperty(key, value);
  };

  // One real media element drives every card, product row and controller.
  const player = {
    audio: new Audio(),
    owner: null, track: null, source: '', wanted: false, buffering: false, pendingPlay: false,
    restoreAutoplay: false, resumeBlocked: false, restoring: false, leaving: false, lastPlaybackSave: 0,
    tabId: Math.random().toString(36).slice(2), tabClaim: null,
    generation: 0, pendingSeek: null, drag: null, error: '', ready: false,
    volume: 1, muted: false, lastVolume: 1,
    get duration() {
      return this.ready && Number.isFinite(this.audio.duration) && this.audio.duration > 0
        ? this.audio.duration : (durations.get(this.source) || 0);
    },
    get time() { return this.drag ? this.drag.target : (this.pendingSeek ?? this.audio.currentTime ?? 0); },
    get playing() { return this.wanted || !this.audio.paused; },
    init() {
      this.audio.preload = 'none';
      this.audio.controls = false;
      try {
        const saved = JSON.parse(sessionStorage.getItem(storageKey));
        if (saved && Number.isFinite(saved.volume) && saved.volume >= 0 && saved.volume <= 1) {
          this.volume = saved.volume;
          this.muted = saved.muted === true;
          this.lastVolume = saved.lastVolume > 0 && saved.lastVolume <= 1
            ? saved.lastVolume : (this.volume || 1);
        }
      } catch {}
      const initial = this.audio.volume;
      try { this.audio.volume = .5; this.deviceVolume = this.audio.volume !== .5; this.audio.volume = initial; }
      catch { this.deviceVolume = true; }
      this.applyVolume();
      this.audio.addEventListener('loadedmetadata', () => {
        this.ready = true;
        if (Number.isFinite(this.audio.duration) && this.audio.duration > 0) durations.set(this.source, this.audio.duration);
        if (this.pendingSeek !== null) this.commitSeek(this.pendingSeek);
        const resume = this.restoreAutoplay;
        this.restoreAutoplay = false;
        this.render();
        if (resume) this.start(true);
        else this.savePlayback(true);
      });
      for (const type of ['durationchange', 'timeupdate', 'seeking', 'seeked']) {
        this.audio.addEventListener(type, () => {
          this.render();
          if (type === 'timeupdate') this.savePlayback();
          else if (type === 'seeked') this.savePlayback(true);
        });
      }
      this.audio.addEventListener('play', () => {
        if (!this.wanted) { this.audio.pause(); return; }
        this.render(); this.tick();
      });
      this.audio.addEventListener('playing', () => {
        this.buffering = false; this.render(); this.tick(); this.savePlayback(true);
      });
      this.audio.addEventListener('waiting', () => {
        this.buffering = this.wanted && !this.drag; this.render();
      });
      this.audio.addEventListener('pause', () => {
        cancelAnimationFrame(this.frame);
        if (!this.drag && !this.pendingPlay && this.audio.paused) this.wanted = false;
        this.render();
        if (this.audio.paused && !this.pendingPlay) this.savePlayback(true);
      });
      this.audio.addEventListener('ended', () => {
        this.wanted = false; this.pendingPlay = false; this.buffering = false; this.pendingSeek = null;
        this.generation++; cancelAnimationFrame(this.frame); this.render(); this.savePlayback(true, false);
      });
      this.audio.addEventListener('error', () => {
        if (!this.track || !this.audio.getAttribute('src')) return;
        this.error = this.track.dataset.errorLabel;
        this.pause(); this.clearPlayback(); this.render();
      });
      this.audio.addEventListener('volumechange', () => this.render());
      const position = () => this.schedulePosition();
      window.addEventListener('resize', position, { passive: true });
      document.addEventListener('scroll', position, { passive: true, capture: true });
      document.addEventListener('visibilitychange', () => {
        cancelAnimationFrame(this.frame);
        if (!document.hidden) { this.render(); this.tick(); }
      });
      reduced.addEventListener('change', () => { cancelAnimationFrame(this.frame); this.tick(); });
      mobile.addEventListener('change', () => { this.render(); this.position(); });
      this.connectTabs();
      window.addEventListener('pagehide', () => {
        this.savePlayback(true);
        this.leaving = true;
        this.pause();
        this.channel?.close(); this.channel = null;
      });
      window.addEventListener('pageshow', event => {
        if (!event.persisted) return;
        this.leaving = false;
        this.connectTabs();
        // BFCache may hold a track which was switched or closed on the next page.
        this.restorePlayback();
      });
      document.addEventListener('timpani:media-claim', event => {
        if (event.detail?.provider !== 'preview') this.pause();
      });
      document.addEventListener('play', event => {
        if (event.target instanceof HTMLAudioElement && event.target !== this.audio && this.playing) this.pause();
      }, true);
    },
    ensureUI() {
      if (this.ui) return;
      const ui = document.createElement('section');
      ui.className = 'tb46-player';
      ui.hidden = true;
      ui.setAttribute('role', 'region');
      ui.innerHTML =
        '<div class="tb47-player-inner"><div class="tb46-player-tools">' +
        '<button type="button" data-tb30-toggle aria-pressed="false"><span data-tb30-icon aria-hidden="true">' + icons.play + '</span></button></div>' +
        '<div class="tb46-player-heading"><span data-tb46-title></span></div>' +
        '<output data-tb30-time aria-live="off">0:00 / —:—</output>' +
        '<div class="tb46-seek" data-tb46-seek role="slider" tabindex="0" aria-valuemin="0" aria-valuemax="0" aria-valuenow="0" aria-disabled="true">' +
        '<span class="tb46-rail" aria-hidden="true"><span class="tb46-fill"></span><span class="tb46-thumb"></span></span><span class="tb46-target" data-tb46-target aria-hidden="true"></span></div>' +
        '<div class="tb46-volume"><button type="button" data-tb44-mute aria-pressed="false">' + icons.speaker + '</button>' +
        '<input data-tb44-volume type="range" min="0" max="100" step="1" value="100"></div>' +
        '<button type="button" class="tb46-close" data-tb46-close><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path d="m7 7 10 10M17 7 7 17"/></svg></button>' +
        '<small data-tb46-status role="status"></small></div>';
      this.ui = ui;
      this.uiRefs = this.refs(ui);
      ui.appendChild(this.audio);
      document.body.appendChild(ui);
      const spacer = document.createElement('div');
      spacer.className = 'tb46-player-spacer'; spacer.hidden = true;
      spacer.setAttribute('aria-hidden', 'true');
      (document.querySelector('.page-wrapper') || document.body).appendChild(spacer);
      this.spacer = spacer;
      const abort = new AbortController();
      this.uiAbort = abort;
      this.bindControls(ui, () => this.track, abort.signal);
      ui.querySelector('[data-tb46-close]').addEventListener('click', () => this.close(true), { signal: abort.signal });
      this.resize = new ResizeObserver(() => this.schedulePosition());
      this.resize.observe(ui);
      const stickyCart = document.querySelector('.sticky-add-to-cart__bar');
      if (stickyCart) {
        this.resize.observe(stickyCart);
        this.cartObserver = new MutationObserver(() => this.schedulePosition());
        this.cartObserver.observe(stickyCart, { attributes: true, attributeFilter: ['data-stuck'] });
      }
    },
    refs(root) {
      return {
        root, toggle: root.querySelector('[data-tb30-toggle]'), icon: root.querySelector('[data-tb30-icon]'),
        seek: root.querySelector('[data-tb46-seek]'), time: root.querySelector('[data-tb30-time]'),
        duration: root.querySelector('[data-tb48-duration]'),
        mute: root.querySelector('[data-tb44-mute]'), volume: root.querySelector('[data-tb44-volume]')
      };
    },
    select(owner) {
      this.ensureUI();
      if (this.source !== owner.dataset.audioSrc) {
        this.pause();
        this.source = owner.dataset.audioSrc;
        this.pendingSeek = 0; this.error = ''; this.drag = null; this.ready = false;
        this.audio.preload = 'metadata';
        this.audio.src = this.source;
      }
      this.track = trackData(owner.dataset);
      if (owner instanceof HTMLElement && owner.isConnected) this.owner = owner;
      else if (this.owner?.dataset.audioSrc !== this.source || !this.owner?.isConnected) {
        this.owner = Array.from(previews).find(preview => preview.isConnected && preview.dataset.audioSrc === this.source) || null;
      }
      this.render(); this.position(); this.savePlayback(true);
    },
    toggle(owner = this.track) {
      if (!owner) return;
      const same = this.source === owner.dataset.audioSrc;
      this.select(owner);
      if (same && this.playing) this.pause();
      else this.start();
    },
    async start(restored = false) {
      if (!this.track || this.wanted) return;
      this.resumeBlocked = false;
      this.restoreAutoplay = false;
      document.dispatchEvent(new CustomEvent('timpani:media-claim', { detail: { provider: 'preview' } }));
      this.error = ''; this.wanted = true; this.pendingPlay = true; this.buffering = true;
      if (this.audio.ended) this.audio.currentTime = 0;
      const generation = ++this.generation;
      this.applyVolume(); this.render();
      try {
        await this.audio.play();
        if (generation !== this.generation || !this.wanted) return;
        this.pendingPlay = false; this.buffering = this.audio.readyState < 3;
        this.render(); this.tick(); this.savePlayback(true);
        // A successful Play in another tab pauses this tab; no shared storage or polling.
        this.tabClaim = { type: 'play', time: Math.max(Date.now(), (this.tabClaim?.time || 0) + 1), id: this.tabId };
        try { this.channel?.postMessage(this.tabClaim); } catch {}
      } catch (error) {
        if (generation !== this.generation) return;
        this.pendingPlay = false;
        if (error.name === 'AbortError') { this.wanted = false; this.buffering = false; this.render(); this.savePlayback(true, false); return; }
        this.wanted = false; this.buffering = false;
        // Browser-denied autoplay is not an intentional Pause. Keep its intent for the next navigation only.
        this.resumeBlocked = restored && error.name === 'NotAllowedError';
        this.error = this.resumeBlocked ? '' : (this.track?.dataset.errorLabel || '');
        this.render();
        if (this.error) this.clearPlayback();
        else this.savePlayback(true, this.resumeBlocked);
      }
    },
    pause() {
      this.resumeBlocked = false;
      this.restoreAutoplay = false;
      this.wanted = false; this.pendingPlay = false; this.buffering = false;
      this.generation++;
      this.audio.pause(); cancelAnimationFrame(this.frame); this.render(); this.savePlayback(true);
    },
    close(restoreFocus = false) {
      const owner = this.owner;
      this.drag = null; this.pendingSeek = null; this.pause();
      this.owner = null; this.track = null; this.source = ''; this.error = ''; this.ready = false;
      this.audio.removeAttribute('src'); this.audio.load();
      this.clearPlayback(); this.render(); this.position();
      if (restoreFocus && owner?.isConnected) owner.refs.toggle?.focus({ preventScroll: true });
    },
    commitSeek(seconds) {
      if (!Number.isFinite(seconds) || this.duration <= 0) return;
      const target = clamp(seconds, 0, this.duration);
      this.pendingSeek = target;
      if (this.ready && Number.isFinite(this.audio.duration) && this.audio.duration > 0 && this.audio.readyState > 0) {
        try { this.audio.currentTime = target; this.pendingSeek = null; } catch {}
      }
    },
    setVolume(percent) {
      this.volume = clamp(Number(percent) / 100, 0, 1);
      this.muted = this.volume === 0;
      if (this.volume > 0) this.lastVolume = this.volume;
      this.applyVolume(); this.persist(); this.render();
    },
    mute() {
      if (this.muted || this.volume === 0) { this.volume = this.lastVolume || 1; this.muted = false; }
      else { this.lastVolume = this.volume; this.muted = true; }
      this.applyVolume(); this.persist(); this.render();
    },
    applyVolume() {
      if (!this.deviceVolume) this.audio.volume = this.volume;
      this.audio.muted = this.muted || this.volume === 0;
    },
    persist() {
      try { sessionStorage.setItem(storageKey, JSON.stringify({ volume: this.volume, muted: this.muted, lastVolume: this.lastVolume })); } catch {}
    },
    connectTabs() {
      if (this.channel || typeof BroadcastChannel !== 'function') return;
      try {
        this.channel = new BroadcastChannel('timpani-preview-playback-v1');
        this.channel.onmessage = event => {
          const claim = event.data;
          if (claim?.type !== 'play' || !Number.isFinite(claim.time) || typeof claim.id !== 'string' || claim.id === this.tabId) return;
          const newer = !this.tabClaim || claim.time > this.tabClaim.time
            || (claim.time === this.tabClaim.time && claim.id > this.tabClaim.id);
          if (newer) {
            this.tabClaim = claim;
            if (this.playing || this.restoreAutoplay || this.resumeBlocked) this.pause();
          }
        };
      } catch { this.channel = null; }
    },
    clearPlayback() {
      try { sessionStorage.removeItem(playbackKey); } catch {}
      this.lastPlaybackSave = 0;
    },
    savePlayback(force = false, playing = this.resumeBlocked || this.restoreAutoplay || (this.drag ? this.drag.wasPlaying : this.playing)) {
      if (this.leaving || this.restoring || !this.track || this.error) return;
      const now = Date.now();
      if (!force && now - this.lastPlaybackSave < playbackSaveInterval) return;
      const source = audioURL(this.source);
      if (!source) return;
      const data = this.track.dataset;
      const labels = {};
      for (const key of labelKeys) labels[key] = data[key];
      const state = {
        version: 1, source, title: data.title, productUrl: data.productUrl,
        time: Math.max(0, Number.isFinite(this.time) ? this.time : 0),
        playing: playing === true, savedAt: now, labels
      };
      try { sessionStorage.setItem(playbackKey, JSON.stringify(state)); this.lastPlaybackSave = now; } catch {}
    },
    readPlayback() {
      try {
        const value = sessionStorage.getItem(playbackKey);
        if (!value) return null;
        const state = JSON.parse(value);
        const source = audioURL(state?.source);
        if (state?.version !== 1 || !source || !cleanText(state.title, 200)
          || typeof state.playing !== 'boolean' || !Number.isFinite(state.time) || state.time < 0 || state.time > 86400
          || !Number.isFinite(state.savedAt) || state.savedAt <= 0
          || Date.now() - state.savedAt > playbackMaxAge || state.savedAt > Date.now() + 60000) {
          this.clearPlayback(); return null;
        }
        return { ...state, source };
      } catch { this.clearPlayback(); return null; }
    },
    restorePlayback() {
      const state = this.readPlayback();
      // Repeated lifecycle notifications must not restart a pending, playing, or denied attempt.
      if (state?.playing && state.source === this.source && this.track
        && (this.wanted || this.restoreAutoplay || this.resumeBlocked)) return;
      this.restoring = true;
      this.pause();
      if (!state) {
        this.close(); this.restoring = false; return;
      }
      const labels = {};
      for (const key of labelKeys) labels[key] = state.labels?.[key];
      this.select(trackData({ ...labels, audioSrc: state.source, title: state.title, productUrl: state.productUrl }));
      this.pendingSeek = state.time;
      this.restoreAutoplay = state.playing;
      this.restoring = false;
      if (this.ready) {
        this.commitSeek(state.time);
        this.restoreAutoplay = false;
        if (state.playing) this.start(true);
        else this.savePlayback(true, false);
      }
      this.render(); this.position();
    },
    bindControls(root, getOwner, signal) {
      const options = { signal };
      // Only actual controls intercept events. Artwork/product links retain navigation.
      for (const control of root.querySelectorAll('button,[data-tb46-seek],[data-tb44-volume]')) {
        for (const type of ['click', 'pointerdown', 'keydown']) control.addEventListener(type, event => event.stopPropagation(), options);
      }
      root.querySelector('[data-tb30-toggle]')?.addEventListener('click', () => this.toggle(getOwner()), options);
      root.querySelector('[data-tb44-mute]')?.addEventListener('click', () => this.mute(), options);
      root.querySelector('[data-tb44-volume]')?.addEventListener('input', event => this.setVolume(event.target.value), options);
      const seek = root.querySelector('[data-tb46-seek]');
      if (seek) this.bindSeek(seek, getOwner, signal);
    },
    bindSeek(seek, getOwner, signal) {
      const options = { signal };
      const sticky = !!seek.closest('.tb46-player');
      let pendingTouch = null;
      const target = seek.querySelector('[data-tb46-target]');
      const hover = event => {
        const owner = getOwner();
        const duration = owner && (owner.dataset.audioSrc === this.source ? this.duration : durations.get(owner.dataset.audioSrc));
        if (!duration) return null;
        const rect = seek.getBoundingClientRect();
        const ratio = clamp((event.clientX - rect.left) / Math.max(1, rect.width), 0, 1);
        property(seek, '--tb46-target', (ratio * 100).toFixed(3) + '%');
        text(target, format(ratio * duration));
        return ratio * duration;
      };
      const begin = event => {
        if (this.drag) return false;
        const owner = getOwner();
        if (!owner) return false;
        const duration = owner.dataset.audioSrc === this.source ? this.duration : durations.get(owner.dataset.audioSrc);
        if (!duration) { this.select(owner); return false; }
        const wasPlaying = this.source === owner.dataset.audioSrc && this.playing;
        this.select(owner);
        this.drag = { seek, pointerId: event.pointerId, wasPlaying, target: this.time, startTime: this.time };
        this.pendingPlay = false; this.generation++;
        this.audio.pause(); this.buffering = false;
        seek.classList.add('is-scrubbing');
        try { seek.setPointerCapture(event.pointerId); } catch {}
        if (event.pointerType === 'mouse') event.preventDefault();
        return true;
      };
      const move = event => {
        if (pendingTouch) {
          if (pendingTouch.pointerId !== event.pointerId) return;
          const dx = event.clientX - pendingTouch.x, dy = event.clientY - pendingTouch.y;
          if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
          const owner = pendingTouch.owner;
          pendingTouch = null;
          // Let vertical gestures scroll the page without pausing or seeking audio.
          if (Math.abs(dy) >= Math.abs(dx) || getOwner() !== owner || !begin(event)) return;
        }
        const value = hover(event);
        if (value === null) return;
        if (this.drag?.seek === seek && event.pointerId === this.drag.pointerId) {
          this.drag.target = value;
          this.commitSeek(value);
          this.render();
        } else if (!this.drag && event.pointerType !== 'touch') seek.classList.add('is-hovering');
      };
      seek.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0 || this.drag) return;
        if (sticky && event.pointerType === 'touch') {
          const owner = getOwner();
          if (owner) pendingTouch = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, owner };
          return;
        }
        if (begin(event)) { move(event); this.render(); }
      }, options);
      seek.addEventListener('pointermove', move, options);
      const finish = (event, cancelled = false) => {
        if (pendingTouch?.pointerId === event.pointerId) {
          const owner = pendingTouch.owner;
          pendingTouch = null;
          if (cancelled || getOwner() !== owner || !begin(event)) return;
        }
        const drag = this.drag;
        if (!drag || drag.seek !== seek || drag.pointerId !== event.pointerId) return;
        if (!cancelled) move(event);
        this.commitSeek(cancelled && !sticky ? drag.startTime : drag.target);
        this.drag = null;
        seek.classList.remove('is-scrubbing', 'is-hovering');
        try { if (seek.hasPointerCapture(event.pointerId)) seek.releasePointerCapture(event.pointerId); } catch {}
        this.wanted = false; this.pendingPlay = false;
        if (drag.wasPlaying) this.start();
        else { this.audio.pause(); this.render(); }
      };
      seek.addEventListener('pointerup', event => finish(event), options);
      seek.addEventListener('pointercancel', event => finish(event, true), options);
      // Lost capture has no useful pointer coordinates; keep the last deliberate seek.
      seek.addEventListener('lostpointercapture', event => finish(event, sticky), options);
      seek.addEventListener('pointerleave', () => { if (this.drag?.seek !== seek) seek.classList.remove('is-hovering'); }, options);
      seek.addEventListener('keydown', event => {
        const deltas = { ArrowLeft: -5, ArrowRight: 5, ArrowDown: -5, ArrowUp: 5 };
        if (!(event.key in deltas) && event.key !== 'Home' && event.key !== 'End') return;
        event.preventDefault();
        const owner = getOwner();
        if (!owner) return;
        this.select(owner);
        if (!this.duration) return;
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.duration
          : this.time + deltas[event.key] * (event.shiftKey ? 3 : 1);
        this.commitSeek(next); this.render();
      }, options);
    },
    renderRefs(refs, owner, selected) {
      const duration = selected ? this.duration : (durations.get(owner.dataset.audioSrc) || 0);
      const current = selected ? clamp(this.time, 0, duration || Infinity) : 0;
      const ratio = duration > 0 ? current / duration : 0;
      const playing = selected && this.playing;
      const loading = selected && this.wanted && !this.drag && (this.buffering || this.pendingPlay);
      // Decorative state follows real media playback, never the pending Play intent.
      const audible = selected && !this.audio.paused && !this.audio.ended
        && !this.pendingPlay && !this.buffering && !this.error && this.audio.readyState >= 3;
      refs.root.classList.toggle('is-audio-playing', audible);
      refs.root.classList.toggle('is-playing', playing);
      refs.root.classList.toggle('is-active', selected);
      refs.root.classList.toggle('is-loading', loading);
      const iconState = playing ? 'pause' : 'play';
      if (refs.icon && refs.icon.dataset.state !== iconState) { refs.icon.innerHTML = icons[iconState]; refs.icon.dataset.state = iconState; }
      attr(refs.toggle, 'aria-label', playing ? owner.dataset.pauseLabel : owner.dataset.playLabel);
      attr(refs.toggle, 'aria-pressed', playing);
      if (loading) attr(refs.toggle, 'aria-busy', true);
      else refs.toggle?.removeAttribute('aria-busy');
      property(refs.root, '--tb30-progress', (ratio * 100).toFixed(3) + '%');
      if (refs.seek) {
        property(refs.seek, '--tb46-progress', (ratio * 100).toFixed(3) + '%');
        attr(refs.seek, 'aria-disabled', duration <= 0);
        attr(refs.seek, 'aria-valuemax', duration.toFixed(3));
        attr(refs.seek, 'aria-valuenow', current.toFixed(3));
        attr(refs.seek, 'aria-valuetext', format(current) + ' / ' + format(duration || NaN));
      }
      text(refs.time, format(current) + ' / ' + format(duration || NaN));
      text(refs.duration, format(duration || NaN));
      const silent = this.muted || this.volume === 0;
      attr(refs.mute, 'aria-label', silent ? owner.dataset.unmuteLabel : owner.dataset.muteLabel);
      attr(refs.mute, 'aria-pressed', silent);
      refs.mute?.classList.toggle('is-muted', silent);
      if (refs.volume) {
        refs.volume.hidden = this.deviceVolume;
        const percent = Math.round((silent ? 0 : this.volume) * 100);
        if (refs.volume.value !== String(percent)) refs.volume.value = String(percent);
        attr(refs.volume, 'aria-valuetext', percent + '%');
        property(refs.root, '--tb44-volume', percent + '%');
      }
    },
    render() {
      for (const preview of previews) {
        if (!preview.refs) continue;
        const selected = !!this.track && preview.dataset.audioSrc === this.source;
        this.renderRefs(preview.refs, preview, selected);
        const visibleError = preview === this.owner && (!mobile.matches && preview.isTrack);
        text(preview.querySelector('[data-tb30-status]'), visibleError ? this.error : '');
      }
      if (!this.ui) return;
      const visible = !!this.track;
      const changed = this.ui.hidden === visible;
      this.ui.hidden = !visible;
      if (this.track) {
        attr(this.ui, 'aria-label', this.track.dataset.playerLabel);
        attr(this.uiRefs.seek, 'aria-label', this.track.dataset.seekLabel);
        attr(this.uiRefs.volume, 'aria-label', this.track.dataset.volumeLabel);
        attr(this.ui.querySelector('[data-tb46-close]'), 'aria-label', this.track.dataset.closeLabel);
        text(this.ui.querySelector('[data-tb46-title]'), this.track.dataset.title);
        text(this.ui.querySelector('[data-tb46-status]'), visible ? this.error : '');
        this.renderRefs(this.uiRefs, this.track, true);
      }
      if (changed) this.schedulePosition();
    },
    tick() {
      cancelAnimationFrame(this.frame);
      if (this.audio.paused || document.hidden || reduced.matches) return;
      this.frame = requestAnimationFrame(() => { this.render(); this.tick(); });
    },
    schedulePosition() {
      if (this.positionFrame) return;
      this.positionFrame = requestAnimationFrame(() => { this.positionFrame = 0; this.position(); });
    },
    position() {
      const productPage = !!document.querySelector('.product-information');
      const productCart = productPage && document.querySelector('.sticky-add-to-cart__bar');
      if (!this.ui || this.ui.hidden || !this.track) {
        if (productCart) {
          productCart.removeAttribute('data-tb50-audio-active');
          productCart.style.removeProperty('--tb50-player-height');
        }
        if (this.spacer) this.spacer.hidden = true;
        return;
      }
      if (productPage) {
        // Playback owns the viewport edge; only the purchase bar is offset.
        this.ui.classList.add('is-docked', 'is-product-docked');
        this.ui.style.removeProperty('--tb46-browser-ui');
        property(this.ui, 'left', '0px');
        property(this.ui, 'right', '0px');
        property(this.ui, 'top', 'auto');
        property(this.ui, 'bottom', '0px');
        const height = this.ui.getBoundingClientRect().height;
        let cartFootprint = 0;
        if (productCart) {
          property(productCart, '--tb50-player-height', height + 'px');
          attr(productCart, 'data-tb50-audio-active', true);
          const cartStyle = getComputedStyle(productCart);
          if (productCart.dataset.stuck === 'true' && cartStyle.display !== 'none') {
            const gap = Math.max(0, (parseFloat(cartStyle.bottom) || height) - height);
            cartFootprint = productCart.offsetHeight + gap;
          }
        }
        this.spacer.hidden = false;
        property(this.spacer, 'block-size', (height + cartFootprint + 16) + 'px');
        return;
      }
      const bar = document.getElementById('PBarNextFrame');
      const barRect = bar?.getBoundingClientRect();
      const barStyle = bar && getComputedStyle(bar);
      const barHeight = barRect && barStyle.display !== 'none' && barStyle.visibility !== 'hidden'
        && barRect.bottom >= innerHeight - 2 && barRect.top < innerHeight ? Math.min(barRect.height, 80) : 0;
      const stickyCart = document.querySelector('.sticky-add-to-cart__bar[data-stuck="true"]');
      const cartStyle = stickyCart && getComputedStyle(stickyCart);
      const cartHeight = stickyCart && cartStyle.display !== 'none'
        ? stickyCart.offsetHeight + (parseFloat(cartStyle.bottom) || 0) + 8 : 0;
      const bottomOccupied = Math.max(barHeight, cartHeight);
      property(this.ui, '--tb46-browser-ui', bottomOccupied + 'px');
      // The expanded controller belongs to the viewport, never to a product card.
      this.ui.classList.add('is-docked');
      property(this.ui, 'left', '0px');
      property(this.ui, 'right', '0px');
      property(this.ui, 'top', 'auto');
      property(this.ui, 'bottom', 'var(--tb46-browser-ui, 0px)');
      const height = this.ui.getBoundingClientRect().height;
      this.spacer.hidden = false;
      property(this.spacer, 'block-size', (height + bottomOccupied + 16) + 'px');
    },

  };
  player.init();

  class ProductPreview extends HTMLElement {
    connectedCallback() {
      if (this.abort || !this.dataset.audioSrc) return;
      this.dataset.audioSrc = audioURL(this.dataset.audioSrc) || this.dataset.audioSrc;
      const ui = this.querySelector('.tb30-preview-ui');
      if (!ui || !this.querySelector('[data-tb30-toggle]')) return;
      this.abort = new AbortController();
      this.refs = player.refs(this);
      this.isTrack = this.classList.contains('tb42-preview-track');
      // Reuse optional artwork accents; unset cards inherit the single CSS fallback.
      const artwork = this.closest('.tb38-artwork, .tb75-search-item');
      if (artwork) {
        for (const accent of ['primary', 'secondary']) {
          const color = this.dataset[accent === 'primary' ? 'audioPrimary' : 'audioSecondary'];
          if (color && CSS.supports('color', color)) property(artwork, '--tb106-audio-' + accent, color);
        }
      }
      previews.add(this);
      player.bindControls(this, () => this, this.abort.signal);
      const deviceHint = this.querySelector('[data-tb44-device-volume]');
      if (deviceHint) deviceHint.hidden = !player.deviceVolume;
      ui.hidden = false;
      this.controlsUI = ui;
      // Horizon can morph server-rendered children without reconnecting this element.
      // Rebind only replaced/reset controls; aborting the old listeners prevents double playback.
      this.domObserver = new MutationObserver(() => {
        const currentUI = this.querySelector('.tb30-preview-ui');
        const toggle = this.querySelector('[data-tb30-toggle]');
        if (!this.isConnected || !currentUI || !toggle) return;
        if (currentUI === this.controlsUI && toggle === this.refs?.toggle && !currentUI.hidden) return;
        this.abort?.abort();
        this.abort = new AbortController();
        this.refs = player.refs(this);
        this.controlsUI = currentUI;
        player.bindControls(this, () => this, this.abort.signal);
        currentUI.hidden = false;
        player.render();
      });
      this.domObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
      player.render();
    }
    disconnectedCallback() {
      this.domObserver?.disconnect();
      this.controlsUI = null;
      if (player.owner === this) {
        const replacement = Array.from(previews).find(preview => preview !== this && preview.isConnected && preview.dataset.audioSrc === player.source);
        if (replacement) player.select(replacement);
        else { player.owner = null; player.render(); }
      }
      previews.delete(this);
      this.abort?.abort(); this.abort = null; this.refs = null;
    }
  }
  customElements.define('tb-audio-preview', ProductPreview);
  player.restorePlayback();
})();
